import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Loader2, UserPlus } from 'lucide-react';
import { ShareholderDialog } from '@/components/shareholder/ShareholderDialog';
import * as CapLedgerService from '@/services/capLedgerService';
import { planDirectLedgerEvent } from '@/utils/ledgerEventShares';
import { parseShareCount } from '@/utils/shareCount';

interface LedgerEventDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const LedgerEventDialog = ({ open, onOpenChange, onSuccess }: LedgerEventDialogProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState<Array<{ id: string; name: string }>>([]);
  const [shareholders, setShareholders] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedShareholder, setSelectedShareholder] = useState('');
  const [eventType, setEventType] = useState<
    'ISSUANCE' | 'TRANSFER' | 'CANCELLATION' | 'SAFE_CONVERSION'
  >('ISSUANCE');
  const [transferee, setTransferee] = useState('');
  const [shares, setShares] = useState('');
  const [pricePerShare, setPricePerShare] = useState('');
  const [shareClass, setShareClass] = useState('Ordinary');
  const [noteReference, setNoteReference] = useState('');
  const [shareholderDialogOpen, setShareholderDialogOpen] = useState(false);

  const isTransfer = eventType === 'TRANSFER';

  useEffect(() => {
    if (open) {
      fetchCompanies();
    }
  }, [open]);

  useEffect(() => {
    if (selectedCompany) {
      fetchShareholders(selectedCompany);
    }
  }, [selectedCompany]);

  const fetchCompanies = async () => {
    try {
      const data = await CapLedgerService.getCompanies();
      setCompanies(data.map((c) => ({ id: c.id, name: c.name })));
    } catch (error: any) {
      toast({
        title: 'Error loading companies',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const fetchShareholders = async (companyId: string) => {
    try {
      const data = await CapLedgerService.getShareholders(companyId);
      setShareholders(data.map((s) => ({ id: s.id, name: s.name })));
    } catch (error: any) {
      toast({
        title: 'Error loading shareholders',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedCompany || !selectedShareholder || !shares) {
      toast({
        title: 'Missing fields',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }

    if (isTransfer && !transferee) {
      toast({
        title: 'Who is receiving the shares?',
        description: 'A transfer moves shares between two holders, so it needs both.',
        variant: 'destructive',
      });
      return;
    }

    // Reachable by choosing a transferee and then changing the transferor to
    // match. record_share_transfer refuses this too; catching it here says so in
    // words rather than as a raised exception.
    if (isTransfer && transferee === selectedShareholder) {
      toast({
        title: 'A transfer needs two different holders',
        description: 'The transferor and the transferee are the same shareholder.',
        variant: 'destructive',
      });
      return;
    }

    // The quantity is a magnitude; the sign belongs to the event type. A transfer
    // is planned separately because it is not one row.
    const plan = isTransfer ? null : planDirectLedgerEvent(eventType, shares);
    if (plan?.kind === 'invalid') {
      toast({ title: 'Invalid share count', description: plan.error, variant: 'destructive' });
      return;
    }

    const transferShares = isTransfer ? parseShareCount(shares) : null;
    if (transferShares?.kind === 'invalid') {
      toast({
        title: 'Invalid share count',
        description: transferShares.error,
        variant: 'destructive',
      });
      return;
    }
    if (transferShares?.kind === 'count' && transferShares.value <= 0) {
      toast({
        title: 'Invalid share count',
        description: 'Enter a number of shares greater than zero',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      if (isTransfer) {
        // Both legs, atomically, with the seller's holding checked. Never
        // insertLedgerEvent: a lone positive TRANSFER row passes the CHECK
        // constraint and mints the shares it was supposed to move.
        await CapLedgerService.recordShareTransfer({
          companyId: selectedCompany,
          fromShareholderId: selectedShareholder,
          toShareholderId: transferee,
          shares: (transferShares as { value: number }).value,
          pricePerShare: pricePerShare ? parseFloat(pricePerShare) : undefined,
          shareClass: shareClass,
          noteReference: noteReference || undefined,
        });
      } else {
        await CapLedgerService.insertLedgerEvent({
          companyId: selectedCompany,
          shareholderId: selectedShareholder,
          eventType: eventType,
          shares: (plan as { shares: number }).shares,
          pricePerShare: pricePerShare ? parseFloat(pricePerShare) : undefined,
          shareClass: shareClass,
          noteReference: noteReference || undefined,
        });
      }

      toast({
        title: 'Success',
        description: isTransfer
          ? 'Transfer recorded on both sides'
          : 'Ledger event created successfully',
      });

      onSuccess();
      onOpenChange(false);
      resetForm();
    } catch (error: any) {
      toast({
        title: 'Error creating ledger event',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedCompany('');
    setSelectedShareholder('');
    setEventType('ISSUANCE');
    setTransferee('');
    setShares('');
    setPricePerShare('');
    setShareClass('Ordinary');
    setNoteReference('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Record Ledger Event</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="company">Company *</Label>
            <Select value={selectedCompany} onValueChange={setSelectedCompany}>
              <SelectTrigger>
                <SelectValue placeholder="Select company" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((company) => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="shareholder">
                {isTransfer ? 'Transfer from *' : 'Shareholder *'}
              </Label>
              {selectedCompany && shareholders.length === 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShareholderDialogOpen(true)}
                  className="h-auto p-0 text-xs"
                >
                  <UserPlus className="mr-1 h-3 w-3" />
                  Add shareholder
                </Button>
              )}
            </div>
            <Select
              value={selectedShareholder}
              onValueChange={setSelectedShareholder}
              disabled={!selectedCompany}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    selectedCompany && shareholders.length === 0
                      ? 'No shareholders - add one first'
                      : 'Select shareholder'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {shareholders.map((shareholder) => (
                  <SelectItem key={shareholder.id} value={shareholder.id}>
                    {shareholder.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isTransfer && (
            <div className="space-y-2">
              <Label htmlFor="transferee">Transfer to *</Label>
              <Select value={transferee} onValueChange={setTransferee} disabled={!selectedCompany}>
                <SelectTrigger id="transferee">
                  <SelectValue placeholder="Select the receiving shareholder" />
                </SelectTrigger>
                <SelectContent>
                  {shareholders
                    .filter((s) => s.id !== selectedShareholder)
                    .map((shareholder) => (
                      <SelectItem key={shareholder.id} value={shareholder.id}>
                        {shareholder.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Both sides are recorded together: the transferor is debited and the transferee
                credited, so the company&apos;s total is unchanged.
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="eventType">Event Type *</Label>
            <Select value={eventType} onValueChange={(value: any) => setEventType(value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ISSUANCE">Issuance</SelectItem>
                <SelectItem value="TRANSFER">Transfer</SelectItem>
                <SelectItem value="CANCELLATION">Cancellation</SelectItem>
                <SelectItem value="SAFE_CONVERSION">SAFE Conversion</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="shares">Shares *</Label>
              <Input
                id="shares"
                type="number"
                value={shares}
                onChange={(e) => setShares(e.target.value)}
                placeholder="1000"
                required
                className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="pricePerShare">Price per Share</Label>
              <Input
                id="pricePerShare"
                type="number"
                step="0.01"
                value={pricePerShare}
                onChange={(e) => setPricePerShare(e.target.value)}
                placeholder="10.00"
                className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="shareClass">Share Class</Label>
            <Input
              id="shareClass"
              value={shareClass}
              onChange={(e) => setShareClass(e.target.value)}
              placeholder="Ordinary"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="noteReference">Note Reference</Label>
            <Input
              id="noteReference"
              value={noteReference}
              onChange={(e) => setNoteReference(e.target.value)}
              placeholder="Optional reference"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="flex-1">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Event
            </Button>
          </div>
        </form>

        <ShareholderDialog
          open={shareholderDialogOpen}
          onOpenChange={setShareholderDialogOpen}
          onSuccess={() => {
            if (selectedCompany) {
              fetchShareholders(selectedCompany);
            }
          }}
          companies={companies}
          preselectedCompanyId={selectedCompany}
          // This form records the shares itself the moment the dialog closes.
          // Offering a count here as well would issue them twice.
          allowShareEntry={false}
        />
      </DialogContent>
    </Dialog>
  );
};
